import generateProfileIcon from '@root/assets/img/regenerate.png';
// import ClustersIcon from '@root/assets/svg/Dashboard/ClustersIcon';
import WorkloadVulnerabilities from '@root/assets/svg/Kubernetes/WorkloadVulnerabilities';
import info from '@root/assets/svg/workload/info.svg';
import question1 from '@root/assets/svg/workload/question1.png';
import Text from '@root/components/v2/ui-elements/Text';
import Tooltip from '@root/components/v2/ui-elements/Tooltip';
import YesNo from '@root/components/YesNo';

import { Box } from '@mui/material';
import {
  severityDarkColors,
  severityLightColors,
  SeverityWithText,
} from '@root/components/bootstrapTable/badges/severity';
// import commonTableColumns from '@root/components/table/commonTableColumns';
import DateFormatPipe from '@root/components/utilities/DateFormatPipe';

// One row per CVE (cves-list API); the workload/image/component breakdown lives in the side overlay.
export const k8columns = (handleOpenModal, setSelectedActionData) => {
  return [
    // The cluster cell carries its account beneath it, from the row's first `clusters` entry.
    // {
    //   ...commonTableColumns.resources({
    //     field: 'clusters',
    //     headerName: 'Cluster',
    //     nameKey: 'cluster',
    //     // Same cluster icon as the dashboard's Kubernetes Security Posture card, on a tint of its purple.
    //     icon: <ClustersIcon width="20px" height="20px" />,
    //     iconBackground: 'rgba(147, 51, 234, 0.12)',
    //   }),
    //   sortable: false,
    // },

    {
      field: 'cve_id',
      headerName: 'CVE ID',
      classes: 'align-middle',
      minWidth: 200,
      sort: true,
      renderCell: params => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Box
            sx={{
              background: 'rgb(243, 244, 246)',
              padding: '5px',
              borderRadius: '.5rem',
            }}>
            <WorkloadVulnerabilities height="16px" width="16px" />
          </Box>
          <Tooltip title={params.row?.cve_id}>
            <Text fontWeight={500} style={{ width: '170px' }} className="SingleLine">
              {params.row?.cve_id}
            </Text>
          </Tooltip>
        </div>
      ),
    },
    {
      field: 'cve_type',
      headerName: 'CVE Type',
      classes: 'align-middle',
      minWidth: 200,
      sort: true,
      renderCell: params => (
        <Tooltip title={params.row?.cve_type}>
          <Text fontWeight={500} style={{ width: '170px' }} className="SingleLine">
            {params.row?.cve_type}
          </Text>
        </Tooltip>
      ),
    },

    {
      field: 'severity',
      headerName: 'CVSS Severity',
      classes: 'align-middle',
      minWidth: 200,
      sort: true,
      renderCell: params => {
        const severity = params.row?.severity?.toUpperCase();
        return (
          <Tooltip title={params.row.severity}>
            {severity !== 'INFORMATIONAL' && severity !== 'UNTRIAGED' ? (
              <SeverityWithText propsValue={params.row?.severity} />
            ) : severity === 'INFORMATIONAL' ? (
              <img src={info} style={{ height: '30px', width: '30px' }} />
            ) : (
              <img src={question1} style={{ height: '30px', width: '30px' }} />
            )}
          </Tooltip>
        );
      },
    },

    {
      field: 'cvss_score',
      headerName: 'CVSS Score',
      classes: 'align-middle',
      minWidth: 200,
      sort: true,
      renderCell: params => {
        if (params.row?.cvss_score == null) return '-';
        const score = parseFloat(params.row.cvss_score).toFixed(1);

        const colorSeverity = params.row?.severity;
        const backgroundDarkColor = severityDarkColors[colorSeverity?.toUpperCase()] || severityDarkColors.DEFAULT;
        const backgroundLightColor = severityLightColors[colorSeverity?.toUpperCase()] || severityLightColors.DEFAULT;

        const version = `v${params.row?.cvss_vector?.match(/CVSS:(\d+\.\d+)/)?.[1] ?? '3.0'}`;

        return (
          <div style={{ display: 'flex', alignItems: 'center', borderRadius: '5px', overflow: 'hidden' }}>
            <div
              style={{
                backgroundColor: backgroundDarkColor,
                padding: '3px 7px',
                color: '#FFFFFF', // Dark gray for better readability on pastel backgrounds
                fontFamily: 'Manrope',
                fontWeight: 500,
                fontSize: '12px',
              }}>
              {score}
            </div>
            <div
              style={{
                backgroundColor: backgroundLightColor,
                padding: '3px 7px',
                color: '#5D0E61', // Dark gray for better readability
                fontFamily: 'Manrope',
                fontWeight: 500,
                fontSize: '12px',
              }}>
              {version}
            </div>
          </div>
        );
      },
    },

    {
      field: 'epss_score',
      headerName: 'EPSS Score',
      classes: 'align-middle',
      minWidth: 150,
      sortable: false,
      // The API sends the EPSS probability as a string, e.g. "0.001480000".
      renderCell: params =>
        params.row?.epss_score == null ? (
          '-'
        ) : (
          <Tooltip title={params.row.epss_score}>
            <Text fontWeight={500} className="SingleLine">
              {(parseFloat(params.row.epss_score) * 100).toFixed(2)}%
            </Text>
          </Tooltip>
        ),
    },

    {
      field: 'cvss_attack_vector',
      headerName: 'Attack Vector',
      classes: 'align-middle',
      minWidth: 150,
      sortable: false,
      renderCell: params => params.row?.cvss_attack_vector ?? '-',
    },

    {
      field: 'exploitable',
      headerName: 'Exploitable',
      classes: 'align-middle',
      minWidth: 120,
      sortable: false,
      renderCell: params => <Text font="medium">{params?.row?.exploitable ? <YesNo yes /> : <YesNo no />}</Text>,
    },
    {
      field: 'exploit_maturity',
      headerName: 'Exploit Maturity',
      classes: 'align-middle',
      minWidth: 150,
      sortable: false,
      renderCell: params => params.row?.exploit_maturity ?? '-',
    },
    {
      field: 'cisa_kev',
      headerName: 'CISA KEV',
      classes: 'align-middle',
      minWidth: 120,
      sortable: false,
      renderCell: params => <Text font="medium">{params?.row?.cisa_kev ? <YesNo yes /> : <YesNo no />}</Text>,
    },
    {
      field: 'fixable',
      headerName: 'Fixable',
      classes: 'align-middle',
      minWidth: 120,
      sortable: false,
      renderCell: params => <Text font="medium">{params?.row?.fixable ? <YesNo yes /> : <YesNo no />}</Text>,
    },
    {
      field: 'in_use',
      headerName: 'In Use',
      classes: 'align-middle',
      minWidth: 120,
      sortable: false,
      // Rows without the field (no runtime sensor on the cluster) show "-" rather than a misleading "No".
      renderCell: params =>
        params?.row?.in_use == null ? (
          '-'
        ) : (
          <Text font="medium">{params.row.in_use ? <YesNo yes /> : <YesNo no />}</Text>
        ),
    },

    {
      field: 'workload_count',
      headerName: 'Workloads',
      classes: 'align-middle',
      minWidth: 120,
      sortable: false,
      renderCell: params => params.row?.workload_count ?? 0,
    },
    {
      field: 'image_count',
      headerName: 'Images',
      classes: 'align-middle',
      minWidth: 120,
      sortable: false,
      renderCell: params => params.row?.image_count ?? 0,
    },
    {
      field: 'component_count',
      headerName: 'Components',
      classes: 'align-middle',
      minWidth: 120,
      sortable: false,
      renderCell: params => params.row?.component_count ?? 0,
    },

    {
      field: 'discovered_at',
      headerName: 'Discovered At',
      renderCell: params => DateFormatPipe(params?.row?.discovered_at) ?? '-',
      width: 250,
    },
    {
      field: 'last_modified',
      headerName: 'Last Modified',
      renderCell: params => DateFormatPipe(params?.row?.last_modified) ?? '-',
      width: 250,
    },

    {
      field: 'action',
      headerName: 'Action',
      classes: 'align-middle',
      minWidth: 200,
      sort: true,
      renderCell: params => (
        <div
          data-testid="click-k8s-pages-cves-k8columns-255"
          data-clickable="true"
          onClick={e => {
            e.stopPropagation();
            setSelectedActionData(params.row);
            if (params.row.affected_workloads?.length > 0) {
              handleOpenModal();
            }
          }}>
          <img src={generateProfileIcon} height="18px" width="18px" />
        </div>
      ),
    },
  ];
};

export const stackedLayersColumn = () => {
  return [
    {
      field: 'cve_id',
      headerName: 'CVE ID',
      classes: 'align-middle',
      // headerClassName: 'fs__2'
      minWidth: 200,
      sort: true,
      renderCell: params => {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Box sx={{ background: 'rgb(243, 244, 246)', padding: '5px', borderRadius: '.5rem' }}>
              {' '}
              <WorkloadVulnerabilities height="16px" width="16px" />{' '}
            </Box>

            <Tooltip title={params.row?.cve_id}>
              <Text fontWeight={500} style={{ width: '170px' }} className="SingleLine">
                {params.row?.cve_id}
              </Text>
            </Tooltip>
          </div>
        );
      },
    },

    {
      field: 'severity',
      headerName: 'CVSS Severity',
      classes: 'align-middle',
      // headerClassName: 'fs__2'
      minWidth: 150,
      sort: true,
      renderCell: params => {
        return (
          <Tooltip title={params.row.severity}>
            {params.row?.severity?.toUpperCase() !== 'INFORMATIONAL' ||
            params.row?.severity?.toUpperCase() !== 'UNTRIAGED' ? (
              // <HighIcon textDisabled={true} />
              <SeverityWithText propsValue={params.row?.severity} />
            ) : params.row?.severity?.toUpperCase() === 'INFORMATIONAL' ? (
              <img
                src={info}
                style={{
                  height:
                    params.row?.severity?.toUpperCase() === 'INFORMATIONAL' ||
                    params.row?.severity?.toUpperCase() === 'UNTRIAGED'
                      ? '30px'
                      : '',
                  width:
                    params.row?.severity?.toUpperCase() === 'INFORMATIONAL' ||
                    params.row?.severity?.toUpperCase() === 'UNTRIAGED'
                      ? '30px'
                      : '',
                }}
              />
            ) : (
              <img
                src={question1}
                style={{
                  height:
                    params.row?.severity?.toUpperCase() === 'INFORMATIONAL' ||
                    params.row?.severity?.toUpperCase() === 'UNTRIAGED'
                      ? '30px'
                      : '',
                  width:
                    params.row?.severity?.toUpperCase() === 'INFORMATIONAL' ||
                    params.row?.severity?.toUpperCase() === 'UNTRIAGED'
                      ? '30px'
                      : '',
                }}
              />
            )}
          </Tooltip>
        );
      },
    },
    {
      field: 'cvss_score',
      headerName: 'CVSS Score',
      classes: 'align-middle',
      minWidth: 150,
      sort: true,
      renderCell: params => {
        const score =
          params.row?.['cvss_score'] === 0 || !params.row?.['cvss_score']
            ? 0
            : parseFloat(params.row?.['cvss_score']).toFixed(1);
        const colorSeverity = params.row?.severity;

        const backgroundDarkColor = severityDarkColors[colorSeverity?.toUpperCase()] || severityDarkColors.DEFAULT;
        const backgroundLightColor = severityLightColors[colorSeverity?.toUpperCase()] || severityLightColors.DEFAULT;

        // const version = `v${params.row?.details?.Vector ?? '3.0'}`;
        const version = params.row?.details?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.version
          ? `v${params.row?.details?.cvssMetricV31?.cvssMetricV31?.[0]?.cvssData?.version}`
          : params.row?.details?.Vector?.match(/CVSS:(\d+\.\d+)/)?.[1]
            ? `v${params.row?.details?.Vector?.match(/CVSS:(\d+\.\d+)/)[1]}`
            : 'v3.0';

        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              borderRadius: '5px',
              overflow: 'hidden',
            }}>
            <div
              style={{
                backgroundColor: backgroundDarkColor,
                padding: '3px 7px',
                color: '#FFFFFF',
                fontFamily: 'Manrope',
                fontWeight: 500,
                fontSize: '12px',
              }}>
              {score}
            </div>
            <div
              style={{
                backgroundColor: backgroundLightColor,
                padding: '3px 7px',
                color: '#5D0E61',
                fontFamily: 'Manrope',
                fontWeight: 500,
                fontSize: '12px',
              }}>
              {version}
            </div>
          </div>
        );
      },
    },

    {
      field: 'epss_score',
      headerName: 'EPSS Score',
      classes: 'align-middle',
      // headerClassName: 'fs__2'
      minWidth: 150,
      sortable: false,
      renderCell: params => {
        return (
          <Tooltip title={'params.row.threat_trend_data.Vendor'}>
            <Text fontWeight={500} className="SingleLine">
              {(params.row?.exploitability_score ?? params.row?.cve_data?.epss ?? 0).toFixed(2)}
            </Text>
          </Tooltip>
        );
      },
    },

    {
      field: 'fix_version',
      headerName: 'Fix Version',
      renderCell: params => {
        return params?.row?.fix_version ?? 'Not Available';
      },
      width: 100,
    },
  ];
};
